/**
 * Per-organization browser storage.
 *
 * WHY THIS EXISTS (2026-08-05)
 * ---------------------------
 * Three surfaces have now stored organization-specific state under a single global
 * key, and the third is what made it worth extracting:
 *
 *   * the dashboard layout (`dashboard-state-v2`) — a P0. An admin with several orgs
 *     had ONE layout for all of them, and because each licenses different products
 *     the widgets the new org lacked were filtered out and the grid reflowed into the
 *     holes. Reported as "the widgets drift after switching organization", with the
 *     confusing detail that Reset appeared to fix it — reset wrote the NEW org's
 *     default into the same shared key, so switching back handed it to the old one.
 *   * the process setup guide's dismissal (`process-setup-guide-dismissed`) —
 *     dismissing it in an org that finished setup hid it in an org with no process
 *     definitions at all, which is exactly who the guide is for.
 *   * `useRecentlyVisited`, which got it right from the start by storing a
 *     `Record<orgId, T>` under one key. That is the shape copied here.
 *
 * ONE KEY HOLDING A MAP, not `key-<orgId>` per org: one entry to migrate, one to
 * clear on sign-out, and no unbounded key growth as a user joins more workspaces.
 *
 * A null org id is a NO-OP rather than a placeholder key. Between sign-in and
 * OrgGate there is no current org, and writing under `_` would create state nobody
 * can reach and that the next real org might inherit.
 */
import { getCurrentOrgId } from '../api/client';

function readMap<T>(key: string, storage: Storage): Record<string, T> {
  try {
    const raw = storage.getItem(key);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, T>)
      : {};
  } catch {
    // Corrupt or unreadable: fall through to the default rather than throwing on a
    // page the user is trying to read.
    return {};
  }
}

/** This organization's value for `key`, or null. */
export function readOrgScoped<T>(
  key: string,
  orgId: string | null = getCurrentOrgId(),
  storage: Storage = localStorage,
): T | null {
  if (!orgId) return null;
  const map = readMap<T>(key, storage);
  return Object.prototype.hasOwnProperty.call(map, orgId) ? map[orgId] : null;
}

export function writeOrgScoped<T>(
  key: string,
  value: T,
  orgId: string | null = getCurrentOrgId(),
  storage: Storage = localStorage,
): void {
  if (!orgId) return;
  try {
    const map = readMap<T>(key, storage);
    map[orgId] = value;
    storage.setItem(key, JSON.stringify(map));
  } catch {
    // Quota exhausted or privacy mode. A lost preference is not worth an exception.
  }
}

/**
 * Adopt a pre-scoping global value into THIS org, once, then delete it.
 *
 * One-shot on purpose. The first org opened after a deploy keeps whatever the user
 * had been looking at; every other org falls through to its own default. Leaving the
 * legacy key in place would re-seed each org in turn from the same value, which is
 * the original bug wearing a migration's clothes.
 */
export function migrateGlobalToOrgScoped<T>(
  legacyKey: string,
  scopedKey: string,
  parse: (raw: string) => T | null,
  orgId: string | null = getCurrentOrgId(),
  storage: Storage = localStorage,
): T | null {
  if (!orgId) return null;
  let legacy: string | null = null;
  try {
    legacy = storage.getItem(legacyKey);
  } catch {
    return null;
  }
  if (!legacy) return null;
  try {
    storage.removeItem(legacyKey);
  } catch {
    // The read already succeeded; continue.
  }
  const adopted = parse(legacy);
  if (adopted === null) return null;
  writeOrgScoped(scopedKey, adopted, orgId, storage);
  return adopted;
}
